import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { AcquisitionData } from '../../types/Ad.api.type';
import { Card, CardContent, CardHeader } from '../ui/card';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { UserPlus } from 'lucide-react';

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

/** 숫자 메트릭 카드 */
const MetricCard = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <Card>
    <div className="p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </Card>
);

/** x축 날짜 → day만 표시 (3/4) */
const formatDateShort = (dateStr: string | number) => {
  if (!dateStr) return '';
  const d = new Date(String(dateStr));
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/**
 * Acquisition (획득) 섹션
 * ServiceAnalyticsDashboard에서 분리됨
 */
export const AcquisitionSection = () => {
  const { data: acquisition, isLoading } = useQuery<AcquisitionData>({
    queryKey: ['analytics', 'acquisition'],
    queryFn: () => adApi.getAcquisition().then(res => res.data),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        로딩 중...
      </div>
    );
  }

  return (
    <section>
      <div className="flex items-center gap-2 mb-4">
        <UserPlus className="w-5 h-5 text-blue-500" />
        <h3 className="text-lg font-bold text-gray-900">Acquisition (획득)</h3>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <MetricCard label="총 사용자" value={acquisition?.totalUsers?.toLocaleString() ?? '0'} />
        <MetricCard label="이번 주 신규" value={acquisition?.newUsersThisWeek?.toLocaleString() ?? '0'} />
        <MetricCard label="오늘 신규" value={acquisition?.newUsersToday?.toLocaleString() ?? '0'} />
        <MetricCard label="플랫폼 수" value={`${acquisition?.platformRatios?.length ?? 0}개`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* 일별 가입자 추이 */}
        <Card>
          <CardHeader className="pb-2">
            <h4 className="text-sm font-semibold text-gray-700">일별 가입자 추이</h4>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={acquisition?.signupTrend ?? []} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: '11px' }} tickFormatter={formatDateShort} />
                <YAxis stroke="#6B7280" style={{ fontSize: '11px' }} width={35} allowDecimals={false} />
                <Tooltip
                  labelFormatter={(label) => formatDateShort(label as string)}
                  contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }}
                />
                <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6', r: 3 }} name="가입자" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* 오늘 시간대별 가입자 */}
        <Card>
          <CardHeader className="pb-2">
            <h4 className="text-sm font-semibold text-gray-700">오늘 시간대별 가입자</h4>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={acquisition?.hourlySignupToday ?? []} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="hour" stroke="#6B7280" style={{ fontSize: '11px' }} tickFormatter={(h) => `${h}시`} />
                <YAxis stroke="#6B7280" style={{ fontSize: '11px' }} width={30} allowDecimals={false} />
                <Tooltip
                  labelFormatter={(h) => `${h}시`}
                  contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }}
                />
                <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} name="가입자" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* 플랫폼 비율 파이차트 */}
      <Card>
        <CardHeader className="pb-2">
          <h4 className="text-sm font-semibold text-gray-700">가입 플랫폼 비율</h4>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={(acquisition?.platformRatios ?? []).map(r => ({ name: r.platform, value: r.count, ratio: r.ratio }))}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="45%"
                outerRadius={80}
                label={false}
              >
                {(acquisition?.platformRatios ?? []).map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }}
              />
              <Legend
                verticalAlign="bottom"
                formatter={(value) => {
                  const item = (acquisition?.platformRatios ?? []).find(r => r.platform === value);
                  return `${value} ${item?.ratio?.toFixed(1) ?? 0}%`;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </section>
  );
};
